import {
  useRollDiceMutation,
  useStartCardGameMutation,
} from '@/api/queries/game';
import { generateMiniGameCardsOptions } from '@/common/lib/mini-game';
import {
  type DiceGameTileType,
  type RollDiceResponseType,
} from '@/common/types/dice-game';
import {
  setActiveBoard,
  setBoxReward,
  setCurrentRollPower,
  setDiceCount,
  setDiceEnergy,
  setDiceResult,
  setMiniGameCardsOptions,
} from '@/redux/reducers/diceGameReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

interface UseMainDiceGameReturnType {
  boardPosition?: number;
  boardTileList: DiceGameTileType[];
  diceCount?: number;
  energyCount?: number;
  rollMultiplier: 1 | 2 | 5;
  responseHandler: (response: RollDiceResponseType) => Promise<void>;
  rollDiceMutation: ReturnType<typeof useRollDiceMutation>;
  handleChangeEnergyCount: (count: number) => void;
  handleChangeDiceCount: (value: number) => void;
}

const useMainDiceGame = (): UseMainDiceGameReturnType => {
  const [startCardGameRequest] = useStartCardGameMutation();
  const boardTileList = useAppSelector((state) => state.diceGame.board);
  const diceCount = useAppSelector((state) => state.diceGame.user?.diceCount);
  const boardPosition = useAppSelector(
    (state) => state.diceGame.user?.position,
  );
  const rollMultiplier = useAppSelector((state) => state.diceGame.rollPower);
  const energyCount = useAppSelector((state) => state.diceGame.user?.energy);
  const skipAnimation = useAppSelector((state) => state.diceGame.skipAnimation);
  const rollDiceMutation = useRollDiceMutation();
  const dispatch = useAppDispatch();

  const responseHandler = async (
    response: RollDiceResponseType,
  ): Promise<void> => {
    dispatch(setDiceResult(response));

    if (!skipAnimation) {
      await new Promise((resolse) => {
        setTimeout(() => {
          resolse(true);
        }, 500);
      });
    }
    dispatch(setCurrentRollPower(response.rollPower as 1 | 2 | 5));

    const position = response.newPosition;
    const boxRewards = response.reward;

    const reward = boardTileList[position % 20];

    if (reward === 'str' || reward === 'tgem') {
      dispatch(setActiveBoard('miniGame'));
    }

    const cardsTileList = ['cash', 'Resource', 'Material', 'Product'];

    if (cardsTileList.includes(reward)) {
      startCardGameRequest();
      dispatch(setMiniGameCardsOptions(generateMiniGameCardsOptions()));
      dispatch(setActiveBoard('cards'));
    }

    if (reward === 'Box' && boxRewards) {
      dispatch(setBoxReward(boxRewards));
      dispatch(setActiveBoard('box'));
    }

    if (reward === 'Energy') {
      if (skipAnimation) {
        handleChangeEnergyCount(response.energy);
      } else {
        setTimeout(() => handleChangeEnergyCount(response.energy), 700);
      }
    }

    if (reward === 'dice') {
      if (skipAnimation) {
        handleChangeDiceCount(response.diceCount);
      } else {
        setTimeout(() => handleChangeDiceCount(response.diceCount), 1000);
      }
    }
  };

  const handleChangeEnergyCount = (value: number): void => {
    dispatch(setDiceEnergy(value));
  };
  const handleChangeDiceCount = (value: number): void => {
    dispatch(setDiceCount(value));
  };

  return {
    boardPosition,
    boardTileList,
    diceCount,
    responseHandler,
    energyCount,
    rollDiceMutation,
    handleChangeEnergyCount,
    handleChangeDiceCount,
    rollMultiplier,
  };
};

export { useMainDiceGame };
